/**
 * Client-side API for role operations
 * All operations are performed server-side via API calls
 *
 * This module replaces the direct Firestore write operations in user-roles.ts
 * for security reasons.
 */

import { UserRole, UserRoleAssignment } from '@/components/authnz/types/rbac';
import { apiFetch } from '@/lib/api-client';
import { logger } from '@/lib/logging/logger';
import { createUsersRolesTrace, logUsersRolesDebug } from '@/lib/users-roles-debug';

/**
 * Assign a role to a user
 */
export class AssignRoleApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'AssignRoleApiError';
  }
}

const API_FAILURE_CODES = ['offboarded', 'not-invited', 'forbidden', 'step-up-required'] as const;

/** Parse `apiFetch` errors (`HTTP <status>: <code>: <message>`). */
export function parseApiFetchFailure(error: unknown): {
  status: number;
  code?: string;
  message: string;
} {
  const message = error instanceof Error ? error.message : String(error);
  const statusMatch = message.match(/\bHTTP (\d+):/);
  const status = statusMatch ? Number(statusMatch[1]) : 500;
  const codeMatch = message.match(
    new RegExp(`\\b(${API_FAILURE_CODES.join('|')})\\b`),
  );
  let code = codeMatch?.[1];
  if (!code && status === 409 && /deleted email until Restore/i.test(message)) {
    code = 'offboarded';
  }
  return { status, code, message };
}

export async function assignApiRole(
  userId: string | null,
  email: string,
  role: UserRole,
  assignedBy: string,
): Promise<boolean> {
  const result = await assignApiRoleDetailed(userId, email, role, assignedBy);
  return result.ok;
}

export async function assignApiRoleDetailed(
  userId: string | null,
  email: string,
  role: UserRole,
  assignedBy: string,
): Promise<{ ok: true } | { ok: false; status: number; code?: string; message: string }> {
  const traceId = createUsersRolesTrace('assignApiRole');
  const started = performance.now();
  try {
    logUsersRolesDebug('roles.assign', `${traceId} request`, {
      userId: userId ?? '(pre-assign)',
      email,
      role,
      assignedBy,
    });

    const body = await apiFetch<{ error?: string; message?: string }>('/admin/roles/assign', {
      method: 'POST',
      body: JSON.stringify({ userId, email, role, assignedBy }),
    });

    const elapsedMs = Math.round(performance.now() - started);
    logUsersRolesDebug('roles.assign', `${traceId} ok`, {
      elapsedMs,
      email,
      role,
      result: body,
    });
    return { ok: true };
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    const parsed = parseApiFetchFailure(error);
    const { message, status, code } = parsed;
    logUsersRolesDebug('roles.assign', `${traceId} failed`, {
      elapsedMs,
      email,
      role,
      error: message,
      status,
      code,
    });
    logger.error('Failed to assign role via API', {
      error: message,
      userId,
      email,
      role,
      status,
      code,
    });
    return { ok: false, status, code, message };
  }
}

/**
 * Remove a role from a user
 */
export async function removeApiRole(
  userId: string,
  role: UserRole,
  email?: string,
  options?: { confirmSelf?: boolean }
): Promise<boolean> {
  try {
    await apiFetch('/admin/roles/remove', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        role,
        email,
        ...(options?.confirmSelf ? { confirmSelf: true } : {}),
      }),
    });
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error('Failed to remove role via API', { error: message, userId, role, email });
    throw error instanceof Error ? error : new Error(message);
  }
}

/**
 * Get all role assignments
 */
export async function getAllApiRoleAssignments(): Promise<UserRoleAssignment[]> {
  try {
    const data = await apiFetch<
      Array<UserRoleAssignment & { assignedAt?: string; updatedAt?: string }>
    >('/admin/roles/list');

    return data.map((item) => ({
      ...item,
      assignedAt: item.assignedAt ? new Date(item.assignedAt) : undefined,
      updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
    }));
  } catch (error) {
    logger.error('Failed to list roles via API', { error });
    return [];
  }
}
